/**
 * Shared inventory UI components — icons, item cells, and tooltips.
 * Used by both BaseState (Home Base) and LabyrinthState (map sidebar).
 */
import { Container, Graphics, Text } from 'pixi.js';
import { drawBevelPanel, drawInsetRect } from './PanelUtils';
import { INSET_FILL, INSET_RADIUS } from './LayoutConstants';
import type { UpgradeCategory, UpgradeDef } from '../upgrades/UpgradeData';
import type { WeaponCategory, WeaponDef } from '../upgrades/WeaponData';
import type { StatName } from '../upgrades/StatsSystem';
import type { ScoredItem } from '../upgrades/Quality';
import { scoreBand, scoreColor, scoreMult } from '../upgrades/Quality';

// ─── Constants ──────────────────────────────────────────────
export const INV_CELL = 64;
export const INV_GAP = 4;
export const INV_COLS = 5;
export const WPN_SLOT_SIZE = 60;
export const SLOT_SIZE = 64;

/**
 * Compute how many inventory columns fit inside `widthBudget` pixels. Used by
 * the mobile layout where the grid needs to flex to screen width. Always
 * returns at least 3 columns so small items remain tappable.
 */
export function columnsForWidth(widthBudget: number): number {
  const cols = Math.floor((widthBudget + INV_GAP) / (INV_CELL + INV_GAP));
  return Math.max(3, Math.min(INV_COLS, cols));
}

// ─── Score color helper (kept as `qColor` to avoid churning call sites) ──
export function qColor(score: number): number {
  return scoreColor(score);
}

// ─── Stat attribute icon ──────────────────────────────────
export function drawStatIcon(g: Graphics, stat: StatName, size: number, color: number) {
  const s = size, h = s / 2;
  switch (stat) {
    case 'speed': {
      // Triple chevrons pointing right — fast motion
      for (let i = 0; i < 3; i++) {
        const ox = -s * 0.18 + i * s * 0.18;
        g.moveTo(h + ox, h - s * 0.28);
        g.lineTo(h + ox + s * 0.18, h);
        g.lineTo(h + ox, h + s * 0.28);
        g.stroke({ color, width: 2, alpha: 1 - i * 0.2 });
      }
      break;
    }
    case 'perception': {
      // Eye shape — outer almond + iris + pupil
      g.moveTo(h - s * 0.38, h);
      g.quadraticCurveTo(h, h - s * 0.32, h + s * 0.38, h);
      g.quadraticCurveTo(h, h + s * 0.32, h - s * 0.38, h);
      g.closePath();
      g.stroke({ color, width: 1.5 });
      g.circle(h, h, s * 0.16);
      g.stroke({ color, width: 1.5 });
      g.circle(h, h, s * 0.07);
      g.fill(color);
      break;
    }
    case 'energy': {
      // Lightning bolt — angular zigzag
      g.moveTo(h + s * 0.08, h - s * 0.38);
      g.lineTo(h - s * 0.12, h - s * 0.02);
      g.lineTo(h + s * 0.06, h + s * 0.02);
      g.lineTo(h - s * 0.08, h + s * 0.38);
      g.stroke({ color, width: 2.5 });
      // small spark accent
      g.circle(h + s * 0.08, h - s * 0.38, s * 0.04);
      g.fill({ color, alpha: 0.7 });
      g.circle(h - s * 0.08, h + s * 0.38, s * 0.04);
      g.fill({ color, alpha: 0.7 });
      break;
    }
    case 'shield': {
      // Shield crest shape
      g.moveTo(h, h - s * 0.36);
      g.lineTo(h + s * 0.3, h - s * 0.22);
      g.lineTo(h + s * 0.26, h + s * 0.12);
      g.lineTo(h, h + s * 0.36);
      g.lineTo(h - s * 0.26, h + s * 0.12);
      g.lineTo(h - s * 0.3, h - s * 0.22);
      g.closePath();
      g.stroke({ color, width: 2 });
      // inner chevron accent
      g.moveTo(h, h - s * 0.1);
      g.lineTo(h + s * 0.12, h + s * 0.05);
      g.lineTo(h, h + s * 0.16);
      g.lineTo(h - s * 0.12, h + s * 0.05);
      g.closePath();
      g.fill({ color, alpha: 0.35 });
      break;
    }
    case 'structure': {
      // Structural I-beam / frame — industrial look
      // top flange
      g.rect(h - s * 0.3, h - s * 0.34, s * 0.6, s * 0.12);
      g.fill({ color, alpha: 0.6 });
      // bottom flange
      g.rect(h - s * 0.3, h + s * 0.22, s * 0.6, s * 0.12);
      g.fill({ color, alpha: 0.6 });
      // web (vertical center)
      g.rect(h - s * 0.08, h - s * 0.22, s * 0.16, s * 0.44);
      g.fill({ color, alpha: 0.45 });
      // rivet dots
      for (const oy of [-0.28, 0.28]) {
        for (const ox of [-0.2, 0.2]) {
          g.circle(h + ox * s, h + oy * s, s * 0.035);
          g.fill(color);
        }
      }
      break;
    }
  }
}

// ─── Upgrade category icon ─────────────────────────────────
export function drawCategoryIcon(g: Graphics, cat: UpgradeCategory, size: number, color: number) {
  const s = size, h = s / 2;
  switch (cat) {
    case 'engine':
      g.circle(h, h, s * 0.35); g.stroke({ color, width: 2 });
      g.circle(h, h, s * 0.12); g.fill(color);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        g.moveTo(h + Math.cos(a) * s * 0.18, h + Math.sin(a) * s * 0.18);
        g.lineTo(h + Math.cos(a) * s * 0.35, h + Math.sin(a + 0.3) * s * 0.35);
        g.stroke({ color, width: 1.5 });
      }
      break;
    case 'sensors':
      g.ellipse(h, h, s * 0.38, s * 0.22); g.stroke({ color, width: 1.5 });
      g.circle(h, h, s * 0.12); g.fill(color);
      g.moveTo(h - s * 0.38, h); g.lineTo(h + s * 0.38, h); g.stroke({ color, width: 0.8, alpha: 0.5 });
      g.moveTo(h, h - s * 0.22); g.lineTo(h, h + s * 0.22); g.stroke({ color, width: 0.8, alpha: 0.5 });
      break;
    case 'capacitor':
      g.moveTo(h + s * 0.1, h - s * 0.38); g.lineTo(h - s * 0.08, h - s * 0.02);
      g.lineTo(h + s * 0.06, h - s * 0.02); g.lineTo(h - s * 0.1, h + s * 0.38);
      g.lineTo(h + s * 0.08, h + s * 0.02); g.lineTo(h - s * 0.06, h + s * 0.02);
      g.closePath(); g.fill({ color, alpha: 0.8 }); g.stroke({ color, width: 1.2 });
      break;
    case 'shield':
      g.moveTo(h, h - s * 0.38); g.lineTo(h + s * 0.32, h - s * 0.2);
      g.lineTo(h + s * 0.28, h + s * 0.15); g.lineTo(h, h + s * 0.38);
      g.lineTo(h - s * 0.28, h + s * 0.15); g.lineTo(h - s * 0.32, h - s * 0.2);
      g.closePath(); g.stroke({ color, width: 2 });
      g.moveTo(h, h - s * 0.15); g.lineTo(h + s * 0.12, h + s * 0.05);
      g.lineTo(h, h + s * 0.18); g.lineTo(h - s * 0.12, h + s * 0.05);
      g.closePath(); g.stroke({ color, width: 1, alpha: 0.5 });
      break;
    case 'chassis':
      g.roundRect(h - s * 0.3, h - s * 0.35, s * 0.6, s * 0.7, 3); g.stroke({ color, width: 2 });
      g.moveTo(h - s * 0.2, h - s * 0.1); g.lineTo(h + s * 0.2, h - s * 0.1); g.stroke({ color, width: 1, alpha: 0.4 });
      g.moveTo(h - s * 0.2, h + s * 0.1); g.lineTo(h + s * 0.2, h + s * 0.1); g.stroke({ color, width: 1, alpha: 0.4 });
      g.circle(h, h, s * 0.06); g.fill(color);
      break;
  }
}

// ─── Weapon category icon ──────────────────────────────────
export function drawWeaponIcon(g: Graphics, cat: WeaponCategory, size: number, color: number) {
  const s = size, h = s / 2;
  switch (cat) {
    case 'ballistic':
      g.roundRect(h - s * 0.35, h - s * 0.18, s * 0.65, s * 0.1, 2); g.fill({ color, alpha: 0.7 });
      g.roundRect(h - s * 0.35, h + s * 0.08, s * 0.65, s * 0.1, 2); g.fill({ color, alpha: 0.7 });
      g.circle(h + s * 0.35, h - s * 0.13, s * 0.08); g.fill({ color, alpha: 0.9 });
      g.circle(h + s * 0.35, h + s * 0.13, s * 0.08); g.fill({ color, alpha: 0.9 });
      g.roundRect(h - s * 0.38, h - s * 0.25, s * 0.25, s * 0.5, 3); g.fill({ color, alpha: 0.5 }); g.stroke({ color, width: 1 });
      break;
    case 'energy':
      g.moveTo(h - s * 0.35, h); g.lineTo(h + s * 0.38, h); g.stroke({ color, width: 3 });
      for (const oy of [-1, 1]) {
        g.moveTo(h - s * 0.3, h);
        for (let i = 0; i <= 8; i++) g.lineTo(h - s * 0.3 + (i / 8) * s * 0.65, h + Math.sin(i * 1.2) * s * 0.12 * oy);
        g.stroke({ color, width: 1, alpha: 0.6 });
      }
      g.circle(h - s * 0.3, h, s * 0.12); g.fill({ color, alpha: 0.6 }); g.stroke({ color, width: 1.2 });
      break;
    case 'rocket':
      g.moveTo(h + s * 0.38, h); g.lineTo(h + s * 0.1, h - s * 0.1); g.lineTo(h - s * 0.25, h - s * 0.1);
      g.lineTo(h - s * 0.3, h); g.lineTo(h - s * 0.25, h + s * 0.1); g.lineTo(h + s * 0.1, h + s * 0.1);
      g.closePath(); g.fill({ color, alpha: 0.7 }); g.stroke({ color, width: 1.2 });
      g.moveTo(h - s * 0.2, h - s * 0.1); g.lineTo(h - s * 0.3, h - s * 0.25); g.lineTo(h - s * 0.1, h - s * 0.1); g.fill({ color, alpha: 0.5 });
      g.moveTo(h - s * 0.2, h + s * 0.1); g.lineTo(h - s * 0.3, h + s * 0.25); g.lineTo(h - s * 0.1, h + s * 0.1); g.fill({ color, alpha: 0.5 });
      g.circle(h - s * 0.35, h, s * 0.06); g.fill({ color: 0xff6600, alpha: 0.8 });
      break;
    case 'bomb':
      g.circle(h, h + s * 0.05, s * 0.28); g.fill({ color, alpha: 0.6 }); g.stroke({ color, width: 1.5 });
      g.moveTo(h + s * 0.15, h - s * 0.2); g.quadraticCurveTo(h + s * 0.25, h - s * 0.35, h + s * 0.1, h - s * 0.38);
      g.stroke({ color, width: 1.2 });
      g.circle(h + s * 0.1, h - s * 0.38, s * 0.05); g.fill({ color: 0xffcc00, alpha: 0.9 });
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.3;
        g.moveTo(h + Math.cos(a) * s * 0.3, h + s * 0.05 + Math.sin(a) * s * 0.3);
        g.lineTo(h + Math.cos(a) * s * 0.4, h + s * 0.05 + Math.sin(a) * s * 0.4);
        g.stroke({ color, width: 1, alpha: 0.4 });
      }
      break;
    case 'melee':
      g.moveTo(h - s * 0.3, h + s * 0.3); g.lineTo(h + s * 0.3, h - s * 0.3);
      g.lineTo(h + s * 0.35, h - s * 0.25); g.lineTo(h - s * 0.25, h + s * 0.35);
      g.closePath(); g.fill({ color, alpha: 0.7 }); g.stroke({ color, width: 0.8 });
      g.moveTo(h + s * 0.3, h + s * 0.3); g.lineTo(h - s * 0.3, h - s * 0.3);
      g.lineTo(h - s * 0.35, h - s * 0.25); g.lineTo(h + s * 0.25, h + s * 0.35);
      g.closePath(); g.fill({ color, alpha: 0.7 }); g.stroke({ color, width: 0.8 });
      g.circle(h, h, s * 0.1); g.fill({ color, alpha: 0.9 });
      break;
  }
}

// ─── Item cell builders ─────────────────────────────────────

/**
 * Shared implementation for {@link buildUpgradeCell} and {@link buildWeaponCell}.
 * The only difference between the two is which icon drawer is used, so we
 * parameterize that and keep a single source of truth for the cell layout.
 */
function buildItemCell(
  tier: number,
  score: number,
  cellSize: number,
  drawIcon: (g: Graphics, size: number, color: number) => void,
): Container {
  const cell = new Container();
  const color = scoreColor(score);

  const cellBg = new Graphics();
  drawInsetRect(cellBg, 0, 0, cellSize, cellSize, INSET_RADIUS, INSET_FILL, color, 0.6);
  cell.addChild(cellBg);

  const icon = new Graphics();
  drawIcon(icon, 26, color);
  icon.position.set(cellSize / 2 - 13, 4);
  cell.addChild(icon);

  if (cellSize >= 56) {
    const tierText = new Text({ text: `T${tier}`, style: { fontFamily: 'Offside', fontSize: 13, fill: color } });
    tierText.anchor.set(0.5, 0);
    tierText.position.set(cellSize / 2, cellSize - 32);
    cell.addChild(tierText);

    const scoreLabel = new Text({ text: String(score), style: { fontFamily: 'Offside', fontSize: 11, fill: color } });
    scoreLabel.alpha = 0.85;
    scoreLabel.anchor.set(0.5, 0);
    scoreLabel.position.set(cellSize / 2, cellSize - 16);
    cell.addChild(scoreLabel);
  }

  return cell;
}

/** Build a square upgrade inventory cell with icon, tier, score badge */
export function buildUpgradeCell(def: UpgradeDef, item: ScoredItem, cellSize: number = INV_CELL): Container {
  return buildItemCell(def.tier, item.score, cellSize, (g, size, color) => drawCategoryIcon(g, def.category, size, color));
}

/** Build a square weapon inventory cell with icon, tier, score badge */
export function buildWeaponCell(def: WeaponDef, item: ScoredItem, cellSize: number = INV_CELL): Container {
  return buildItemCell(def.tier, item.score, cellSize, (g, size, color) => drawWeaponIcon(g, def.category, size, color));
}

// ─── Tooltip builders ───────────────────────────────────────

/** Create upgrade tooltip container (not yet positioned) */
export function createUpgradeTooltip(def: UpgradeDef, item: ScoredItem): { tip: Container; width: number; height: number } {
  const color = scoreColor(item.score);
  const band = scoreBand(item.score);
  const maxW = 280;
  const tip = new Container();
  tip.eventMode = 'none';
  tip.interactiveChildren = false;
  let y = 6;

  const title = new Text({ text: item.name, style: { fontFamily: 'Offside', fontSize: 15, fill: color, wordWrap: true, wordWrapWidth: maxW - 16 } });
  title.position.set(8, y);
  tip.addChild(title);
  y += title.height + 4;

  const subtitle = new Text({ text: `T${def.tier} · ${band.label} · Score ${item.score}`, style: { fontFamily: 'Offside', fontSize: 12, fill: color } });
  subtitle.alpha = 0.85;
  subtitle.position.set(8, y);
  tip.addChild(subtitle);
  y += subtitle.height + 6;

  const descText = new Text({ text: def.description, style: { fontFamily: 'Offside', fontSize: 13, fill: 0x889999, wordWrap: true, wordWrapWidth: maxW - 16 } });
  descText.position.set(8, y);
  tip.addChild(descText);
  y += descText.height + 6;

  // ── Stat lines ──
  const m = scoreMult(item.score);
  const s = def.stats;
  const statEntries: [string, string][] = [];
  if (s.speedMult !== undefined)          statEntries.push(['Speed',            `×${(s.speedMult * m).toFixed(2)}`]);
  if (s.turnSpeedMult !== undefined)      statEntries.push(['Turn speed',       `×${(s.turnSpeedMult * m).toFixed(2)}`]);
  if (s.detectionBonus !== undefined)     statEntries.push(['Detection',        `+${(s.detectionBonus * m).toFixed(1)}`]);
  if (s.visionBonus !== undefined)        statEntries.push(['Vision',           `+${(s.visionBonus * m).toFixed(1)}`]);
  if (s.maxEnergyBonus !== undefined)     statEntries.push(['Max energy',       `+${Math.round(s.maxEnergyBonus * m)}`]);
  if (s.drainMult !== undefined)          statEntries.push(['Energy drain',     `×${s.drainMult.toFixed(2)}`]);
  if (s.damageReduction !== undefined)    statEntries.push(['Damage reduction', `${(s.damageReduction * 100).toFixed(0)}%`]);
  if (s.weaponSlots !== undefined)        statEntries.push(['Weapon slots',     `${s.weaponSlots}`]);
  if (s.maxStructureBonus !== undefined)  statEntries.push(['Max HP',           `+${Math.round(s.maxStructureBonus * m)}`]);

  if (statEntries.length > 0) {
    for (const [label, value] of statEntries) {
      const keyText = new Text({ text: label, style: { fontFamily: 'Offside', fontSize: 12, fill: 0xaabbcc } });
      keyText.position.set(12, y);
      tip.addChild(keyText);
      const valText = new Text({ text: value, style: { fontFamily: 'Offside', fontSize: 12, fill: 0xddddaa } });
      valText.anchor.set(1, 0);
      valText.position.set(maxW - 10, y);
      tip.addChild(valText);
      y += Math.max(keyText.height, valText.height) + 1;
    }
    y += 4;
  }

  const catText = new Text({ text: def.category.toUpperCase(), style: { fontFamily: 'Offside', fontSize: 12, fill: 0x556677 } });
  catText.position.set(8, y);
  tip.addChild(catText);
  y += catText.height + 6;

  const tipH = y + 2;
  const bg = new Graphics();
  drawBevelPanel(bg, 0, 0, maxW, tipH, { fill: 0x0e1218, border: color, radius: 4, depth: 2, alpha: 0.95 });
  tip.addChildAt(bg, 0);

  return { tip, width: maxW, height: tipH };
}

/** Create weapon tooltip container (not yet positioned) */
export function createWeaponTooltip(def: WeaponDef, item: ScoredItem): { tip: Container; width: number; height: number } {
  const color = scoreColor(item.score);
  const band = scoreBand(item.score);
  const maxW = 300;
  const tip = new Container();
  tip.eventMode = 'none';
  tip.interactiveChildren = false;
  let y = 6;

  const title = new Text({ text: item.name, style: { fontFamily: 'Offside', fontSize: 15, fill: color, wordWrap: true, wordWrapWidth: maxW - 16 } });
  title.position.set(8, y);
  tip.addChild(title);
  y += title.height + 4;

  const subtitle = new Text({ text: `T${def.tier} · ${band.label} · Score ${item.score}`, style: { fontFamily: 'Offside', fontSize: 12, fill: color } });
  subtitle.alpha = 0.85;
  subtitle.position.set(8, y);
  tip.addChild(subtitle);
  y += subtitle.height + 6;

  const descText = new Text({ text: def.description, style: { fontFamily: 'Offside', fontSize: 13, fill: 0x889999, wordWrap: true, wordWrapWidth: maxW - 16 } });
  descText.position.set(8, y);
  tip.addChild(descText);
  y += descText.height + 6;

  // ── Stat lines ──
  const s = def.stats;
  const dm = scoreMult(item.score);
  const statEntries: [string, string][] = [];
  statEntries.push(['Damage',       `${Math.round(s.damage * dm)}`]);
  statEntries.push(['Attack speed',  `${s.attackSpeed}s`]);
  statEntries.push(['Range',         `${s.range} tiles`]);
  if (s.aoeRadius)          statEntries.push(['AOE radius',       `${s.aoeRadius}`]);
  if (s.splashRadius)       statEntries.push(['Splash radius',    `${s.splashRadius}`]);
  if (s.energyCost)         statEntries.push(['Energy cost',      `${s.energyCost}`]);
  if (s.burstCount)         statEntries.push(['Burst',            `×${s.burstCount}`]);
  if (s.projectileCount)    statEntries.push(['Projectiles',      `${s.projectileCount}`]);
  if (s.pierceCount)        statEntries.push(['Pierce',           `${s.pierceCount} enemies`]);
  if (s.knockback)          statEntries.push(['Knockback',        `${s.knockback}`]);
  if (s.pullStrength)       statEntries.push(['Pull strength',    `${s.pullStrength}`]);
  if (s.homingStrength)     statEntries.push(['Homing',           `${s.homingStrength} rad/s`]);
  if (s.fragmentCount)      statEntries.push(['Fragments',        `${s.fragmentCount}`]);
  if (s.spreadAngle)        statEntries.push(['Spread',           `${(s.spreadAngle * 180 / Math.PI).toFixed(0)}°`]);
  if (s.speedMult && s.speedMult !== 1) statEntries.push(['Proj. speed', `×${s.speedMult.toFixed(2)}`]);
  if (s.bonusDamageMult)    statEntries.push(['Bonus damage',     `×${s.bonusDamageMult.toFixed(2)}`]);
  if (s.doubleStrike)       statEntries.push(['Double strike',    'Yes']);
  if (s.damageFalloff)      statEntries.push(['Damage falloff',   'Yes']);
  if (s.lingerDuration)     statEntries.push(['Linger',           `${s.lingerDuration}s @ ${s.lingerDps ?? 0} dps`]);
  if (s.statusEffect) {
    const eff = s.statusEffect;
    const effLabel = eff.type.charAt(0).toUpperCase() + eff.type.slice(1);
    const effVal = eff.type === 'slow' ? `${(eff.magnitude * 100).toFixed(0)}% for ${eff.duration}s`
                 : eff.type === 'burn' ? `${eff.magnitude} dps for ${eff.duration}s`
                 : `${eff.duration}s`;
    statEntries.push([effLabel, effVal]);
  }

  for (const [label, value] of statEntries) {
    const keyText = new Text({ text: label, style: { fontFamily: 'Offside', fontSize: 12, fill: 0xaabbcc } });
    keyText.position.set(12, y);
    tip.addChild(keyText);
    const valText = new Text({ text: value, style: { fontFamily: 'Offside', fontSize: 12, fill: 0xddddaa } });
    valText.anchor.set(1, 0);
    valText.position.set(maxW - 10, y);
    tip.addChild(valText);
    y += Math.max(keyText.height, valText.height) + 1;
  }
  y += 4;

  const catText = new Text({ text: def.category.toUpperCase(), style: { fontFamily: 'Offside', fontSize: 12, fill: 0x665544 } });
  catText.position.set(8, y);
  tip.addChild(catText);
  y += catText.height + 6;

  const tipH = y + 2;
  const bg = new Graphics();
  drawBevelPanel(bg, 0, 0, maxW, tipH, { fill: 0x14120e, border: color, radius: 4, depth: 2, alpha: 0.95 });
  tip.addChildAt(bg, 0);

  return { tip, width: maxW, height: tipH };
}

/**
 * Position a tooltip relative to a cell, keeping it inside the screen.
 *
 * Tries placement quadrants in order (left → right → above → below) and
 * accepts the first one that fits within `MARGIN`px of every edge. If
 * none fit (very small viewport + very tall tip), falls back to a
 * clamped left placement — same behavior as the previous implementation,
 * but the cascade avoids the earlier edge case where a tall tip placed
 * left would get vertically clamped into an overlap with the cell.
 */
export function positionTooltip(
  tip: Container, tipH: number, tipW: number,
  cellX: number, cellY: number,
  screenW: number, screenH: number,
  cellSize: number = INV_CELL,
) {
  const MARGIN = 10;
  const GAP = 6;

  const fits = (x: number, y: number) =>
    x >= MARGIN && x + tipW <= screenW - MARGIN &&
    y >= MARGIN && y + tipH <= screenH - MARGIN;

  // Candidates in order of preference.
  const candidates: Array<[number, number]> = [
    [cellX - tipW - GAP, cellY],                       // left, aligned to cell top
    [cellX + cellSize + GAP, cellY],                   // right, aligned to cell top
    [cellX + cellSize / 2 - tipW / 2, cellY - tipH - GAP],    // above, centered on cell
    [cellX + cellSize / 2 - tipW / 2, cellY + cellSize + GAP], // below, centered on cell
  ];

  for (const [cx, cy] of candidates) {
    if (fits(cx, cy)) {
      tip.position.set(cx, cy);
      return;
    }
  }

  // Fallback: left placement, clamped into the viewport.
  let x = cellX - tipW - GAP;
  let y = cellY;
  if (x < MARGIN) x = MARGIN;
  if (x + tipW > screenW - MARGIN) x = screenW - tipW - MARGIN;
  if (y < MARGIN) y = MARGIN;
  if (y + tipH > screenH - MARGIN) y = screenH - tipH - MARGIN;
  tip.position.set(x, y);
}
